import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, SALES_EMAIL } from '../config';
import { Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BADGE = ['Trial', 'Track', 'Custom'];
const BADGE_STYLE = [
  'bg-[#78fbb3] text-[#002110]',
  'bg-[#d7e2ff] text-[#004492]',
  'bg-[#e7e7f0] text-[#424753]',
];
const BTN_STYLE = [
  'bg-[#e7e7f0] text-[#191b22] hover:bg-[#e2e2eb]',
  'bg-[#4F8EF7] text-white shadow-xl shadow-[#4F8EF7]/20 hover:brightness-110',
  'border-2 border-[#c2c6d5]/30 text-[#191b22] hover:bg-[#f3f3fc]',
];

const CUSTOM_CONSUMER_PLAN = {
  id: 'custom',
  name: 'Custom',
  price: '0',
  billing_interval: 'monthly',
  is_active: true,
  custom: true,
  description: 'Exclusive Data Modeling & Private API Access\nSmart Alerts & Watchlists\nEarly Access to Beta Features',
};

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function AnalystUpgradePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [consumerPlans, setConsumerPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Derive user subscription state
  const isTrialing    = user?.subscription?.status === 'trialing';
  const isPro         = user?.tier === 'pro' && user?.subscription?.status === 'active';
  const trialEligible = user?.trial_eligible ?? false;
  const trialEnd      = formatDate(user?.subscription?.current_period_end);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/payments/subscription/plans/?tenant=analyst_consumer`)
      .then((res) => setConsumerPlans(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const monthly = [
    ...[...consumerPlans]
      .filter((p) => p.billing_interval !== 'yearly' && p.is_active)
      .sort((a, b) => parseFloat(a.price) - parseFloat(b.price))
      .slice(0, 2),
    CUSTOM_CONSUMER_PLAN,
  ];

  const getYearlyPlan = (plan) =>
    consumerPlans.find((p) => p.name === plan.name && p.billing_interval === 'yearly');

  const handleConsumerCTA = (plan, asTrial = false) => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/auth'); return; }
    if (!plan.id) return;
    const yearlyPlan = getYearlyPlan(plan);
    const params = new URLSearchParams({
      token,
      plan_id: plan.id,
      monthly_id: plan.id,
      ...(yearlyPlan ? { yearly_id: yearlyPlan.id } : {}),
      billing: asTrial ? 'trial' : 'monthly',
      plan_type: asTrial ? 'trial' : 'pro',
    });
    navigate(`/checkout?${params.toString()}`);
  };

  // Returns CTA config for each card based on plan properties, not index
  const getCTA = (plan) => {
    const price = parseFloat(plan.price);
    const isProPlan  = !plan.custom && price > 0 && plan.billing_interval === 'monthly';
    const isTrialPlan = !plan.custom && plan.billing_interval === 'weekly';

    if (isProPlan) {
      if (isTrialing) return {
        label: 'Trial Active',
        subtext: trialEnd ? `Free until ${trialEnd} · card charged after` : 'Your trial is active',
        style: 'bg-green-100 text-green-800 cursor-default',
        onClick: () => navigate('/settings'),
      };
      if (isPro) return {
        label: 'Manage Plan',
        subtext: null,
        style: BTN_STYLE[1],
        onClick: () => navigate('/settings'),
      };
      if (trialEligible) return {
        label: 'Start 7-day Free Trial',
        subtext: `Free for 7 days, then $${price}/month. Cancel anytime.`,
        style: BTN_STYLE[1],
        onClick: () => handleConsumerCTA(plan, true),
      };
      return {
        label: 'Subscribe',
        subtext: null,
        style: BTN_STYLE[1],
        onClick: () => handleConsumerCTA(plan, false),
      };
    }

    if (isTrialPlan) return {
      label: trialEligible ? 'Start Free Trial' : 'Trial Unavailable',
      subtext: trialEligible ? '7 days free, card required' : 'You have already used your trial',
      style: trialEligible ? BTN_STYLE[0] : 'bg-gray-100 text-gray-400 cursor-not-allowed',
      onClick: trialEligible ? () => handleConsumerCTA(plan, true) : undefined,
    };

    // Free plan (price === 0, not custom, not weekly)
    if (price === 0 && !plan.custom) return {
      label: 'Start Browsing',
      subtext: null,
      style: BTN_STYLE[0],
      onClick: () => navigate('/chat'),
    };

    return null; // custom handled separately
  };

  return (
    <div className="flex-1 overflow-y-auto bg-[#faf8ff] py-12 px-8">
      <div className="max-w-8xl mx-auto space-y-10">

        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-5xl font-extrabold text-[#191b22] tracking-tighter">Choose Your Edge</h1>
          <p className="text-[#424753] text-lg">
            Bespoke intelligence for retail conviction and institutional compliance.
          </p>
          {isTrialing && (
            <div className="inline-block px-4 py-2 bg-green-50 border border-green-200 rounded-full text-sm text-green-800 font-medium">
              Trial active — free until {trialEnd}. Your card will be charged after your trial ends.
            </div>
          )}
        </div>

        {/* Cards */}
        {loading ? (
          <div className="grid grid-cols-3 gap-8">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-white rounded-3xl h-80 animate-pulse border border-gray-100" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch pt-6">
            {monthly.map((plan, idx) => {
              const price = parseFloat(plan.price);
              const isCustom = plan.custom === true;
              const isProPlan = !isCustom && price > 0 && plan.billing_interval === 'monthly';
              const isTrialPlan = !isCustom && plan.billing_interval === 'weekly';
              const features = plan.description
                ? plan.description.split('\n').filter(Boolean).slice(0, 5)
                : [];
              const isHighlight = plan.highlight || isProPlan;
              const badge = isTrialPlan ? 'Trial' : isProPlan ? 'Track' : isCustom ? 'Custom' : BADGE[idx] ?? plan.name;
              const btnStyle = isProPlan ? BTN_STYLE[1] : isCustom ? BTN_STYLE[2] : BTN_STYLE[0];
              const cta = getCTA(plan);

              return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-3xl p-8 flex flex-col shadow-sm transition-all relative ${isHighlight
                      ? 'border-2 border-[#4F8EF7] shadow-2xl shadow-[#4F8EF7]/5 hover:scale-[1.02]'
                      : 'border border-[#c2c6d5]/30 hover:shadow-md'
                    }`}
                >
                  {isHighlight && !isTrialing && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#4F8EF7] text-white px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] shadow-lg shadow-[#4F8EF7]/30 whitespace-nowrap z-20">
                      Most Popular
                    </div>
                  )}
                  {isHighlight && isTrialing && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-500 text-white px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] shadow-lg whitespace-nowrap z-20">
                      Trial Active
                    </div>
                  )}

                  <div className="mb-8">
                    <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-4 ${isProPlan ? BADGE_STYLE[1] : isCustom ? BADGE_STYLE[2] : BADGE_STYLE[0]}`}>
                      {badge}
                    </span>
                    <h5 className={`text-lg font-bold mb-1 ${isHighlight ? 'text-[#4F8EF7]' : 'text-[#191b22]'}`}>
                      {plan.name}
                    </h5>
                    {isCustom ? (
                      <div>
                        <span className="text-5xl font-black text-[#191b22]">POA</span>
                        <p className="text-[#424753]/60 text-sm font-medium mt-1">Price On Application</p>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-5xl font-black text-[#191b22]">${price === 0 ? '0' : price}</span>
                          <span className="text-[#424753]/60 text-sm font-medium">/mo</span>
                        </div>
                        {isProPlan && trialEligible && !isTrialing && !isPro && (
                          <p className="text-xs text-[#4F8EF7] font-medium mt-1">7-day free trial available</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 space-y-3 mb-8">
                    {features.map((f, i) => (
                      <div key={i} className="flex items-start gap-3 text-sm text-[#424753]">
                        <Check size={16} className="text-[#4F8EF7] flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>

                  {isCustom ? (
                    <a
                      href={`mailto:${SALES_EMAIL}`}
                      className={`mt-auto w-full py-4 rounded-xl font-bold text-sm active:scale-[0.98] transition-all text-center block ${BTN_STYLE[2]}`}
                    >
                      Contact Sales
                    </a>
                  ) : cta ? (
                    <div className="mt-auto">
                      <button
                        onClick={cta.onClick}
                        className={`w-full py-4 rounded-xl font-bold text-sm active:scale-[0.98] transition-all ${cta.style}`}
                      >
                        {cta.label}
                      </button>
                      {cta.subtext && (
                        <p className="text-[11px] text-[#424753]/60 text-center mt-2">{cta.subtext}</p>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
